import {
    Box,
    CarouselNavigationButton,
    Flex,
    Grid,
    GridItem,
    useDisclosure,
    useInterval,
} from "@project/ui";

import type { ModuleGalleryFieldsFragment } from "./__generated/module-gallery.contentful.generated";
import { ModalImage } from "@/components/modals/modal-image";
import { useCallback, useEffect, useRef, useState } from "react";
import { AnimatePresence, motion, useInView } from "framer-motion";
import type { ModuleImageFieldsFragment } from "../module-image/__generated/module-image.contentful.generated";
import { GalleryItem } from "./gallery-item";
import { getGalleryLayoutConfig } from "./galleryLayoutConfig";

import { ModuleSpacer } from "@/components/module-spacer";
import { ModuleHeader } from "@/components/module-header";

export type ModuleGalleryProps = ModuleGalleryFieldsFragment & {
    isEmbedded?: boolean;
};

export const ModuleGallery = ({
    title,
    description,
    mediaCollection,
    isEmbedded,
}: ModuleGalleryProps) => {
    const { isOpen, onOpen, onClose } = useDisclosure();

    const [currentImage, setCurrentImage] = useState<undefined | number>(0);
    const [currentSlide, setCurrentSlide] = useState(0);
    const [slideProgress, setSlideProgress] = useState(0);
    const [startTime, setStartTime] = useState(Date.now());
    const [isPaused, setIsPaused] = useState(false);
    const [elapsed, setElapsed] = useState(0);

    const moduleRef = useRef<HTMLDivElement | null>(null);
    const galleryRef = useRef<HTMLDivElement | null>(null);

    const moduleIsInView = useInView(moduleRef, { once: true });
    const galleryIsInView = useInView(galleryRef);

    const totalItems = mediaCollection?.items.length ?? 0;
    const totalFullSlides = Math.floor(totalItems / 5);
    const remainingItems = totalItems % 5;

    // If remaining items are less than 3, they are not considered a new slide
    const totalSlides = remainingItems >= 3 ? totalFullSlides + 1 : totalFullSlides;

    const hasEnoughSlides = totalSlides > 1;

    const itemsInCurrentSlide =
        mediaCollection?.items?.slice(currentSlide * 5, (currentSlide + 1) * 5)?.length ?? 0;

    const handleNext = useCallback(() => {
        if (hasEnoughSlides) {
            setCurrentSlide((prevSlide) => (prevSlide < totalSlides - 1 ? prevSlide + 1 : 0));
            setStartTime(Date.now()); // Reset the start time
            setSlideProgress(0); // Reset progress
        }
    }, [totalSlides, hasEnoughSlides]);

    const handlePrev = useCallback(() => {
        if (hasEnoughSlides) {
            setCurrentSlide((prevSlide) => (prevSlide > 0 ? prevSlide - 1 : totalSlides - 1));
            setStartTime(Date.now()); // Reset the start time
            setSlideProgress(0); // Reset progress
        }
    }, [totalSlides, hasEnoughSlides]);

    const handleMouseEnter = () => {
        if (hasEnoughSlides) {
            setIsPaused(true);
            setElapsed(Date.now() - startTime);
        }
    };

    const handleMouseLeave = () => {
        if (hasEnoughSlides) {
            setIsPaused(false);
            setStartTime(Date.now() - elapsed);
        }
    };

    // Interval for autoplay feature
    const autoplayInterval = 4000;

    // Condition to run slide interval
    const runSlideInterval = hasEnoughSlides && moduleIsInView && galleryIsInView;

    const handleSlideInterval = () => {
        if (!isPaused) {
            const newElapsed = Date.now() - startTime;
            setElapsed(newElapsed);

            const progress = Math.min(newElapsed / autoplayInterval, 1);
            setSlideProgress(progress * 100); // Convert to percentage

            if (progress >= 1) {
                handleNext();
                setStartTime(Date.now()); // Reset the start time
                setElapsed(0); // Reset elapsed time
            }
        }
    };

    useInterval(handleSlideInterval, runSlideInterval ? 100 : null);

    useEffect(() => {
        if (runSlideInterval) {
            setStartTime(Date.now());
            setElapsed(0);
        }
    }, [runSlideInterval]);

    const openImage = (imageIndex: number) => {
        setCurrentImage(imageIndex);
        onOpen();
    };

    const galleryLayoutConfig = getGalleryLayoutConfig(itemsInCurrentSlide);

    return (
        <>
            <ModuleSpacer
                className="moduleGallery"
                ref={moduleRef}
                {...(isEmbedded && { wrapperProps: { px: { base: 5, md: 10, l: 0 } } })}
            >
                <ModuleHeader title={title} description={description}>
                    {hasEnoughSlides && (
                        <GridItem
                            colSpan={1}
                            gridColumnStart={12}
                            display="flex"
                            justifyContent="flex-end"
                            alignItems="self-end"
                        >
                            <Flex
                                justifyContent="flex-end"
                                gap={7}
                                display={{
                                    base: "none",
                                    md: "flex",
                                }}
                                padding={2}
                            >
                                <CarouselNavigationButton
                                    onClick={handlePrev}
                                    direction="prev"
                                    hideLabel
                                    aria-label="previous"
                                />
                                <CarouselNavigationButton
                                    onClick={handleNext}
                                    direction="next"
                                    hideLabel
                                    aria-label="next"
                                />
                            </Flex>
                        </GridItem>
                    )}
                </ModuleHeader>

                <Box ref={galleryRef}>
                    <AnimatePresence key={currentSlide}>
                        <Grid
                            onMouseEnter={handleMouseEnter}
                            onMouseLeave={handleMouseLeave}
                            templateColumns="repeat(12, 1fr)"
                            gap={{ base: 4, md: 8 }}
                            my={{
                                base: 6,
                                md: 8,
                            }}
                            as={motion.div}
                            initial="initial"
                            animate={moduleIsInView ? "animate" : "initial"}
                            variants={{
                                initial: { opacity: 0 },
                                animate: {
                                    opacity: 1,
                                    transition: { staggerChildren: 0.2 },
                                },
                            }}
                        >
                            {mediaCollection?.items
                                ?.slice(currentSlide * 5, (currentSlide + 1) * 5)
                                .map((galleryItem, key) => {
                                    if (!galleryItem?.asset) {
                                        return null;
                                    }

                                    const config =
                                        galleryLayoutConfig[key % galleryLayoutConfig.length];
                                    const imageIndex = currentSlide * 5 + key;

                                    return (
                                        <GalleryItem
                                            key={
                                                galleryItem?.description ??
                                                galleryItem?.asset[0].public_id
                                            }
                                            onItemClick={() => openImage(imageIndex)}
                                            config={config}
                                            galleryItem={galleryItem as ModuleImageFieldsFragment}
                                        />
                                    );
                                })}
                        </Grid>
                    </AnimatePresence>
                    {hasEnoughSlides && (
                        <Flex justifyContent="center" alignItems="center" marginTop={4} gap={2}>
                            {[...Array(totalSlides)].map((_, index) => {
                                const isActiveSlide = currentSlide === index;
                                return (
                                    <Box
                                        as={motion.button}
                                        type="button"
                                        key={`slide-${index}`}
                                        className={`pagination-item ${isActiveSlide ? "active" : ""}`}
                                        height={2}
                                        animate={{
                                            width: isActiveSlide ? "80px" : "8px",
                                            transition: {
                                                duration: 0.4,
                                                type: "tween",
                                            },
                                        }}
                                        cursor={isActiveSlide ? "default" : "pointer"}
                                        bgColor="grey700"
                                        borderRadius="small"
                                        overflow="hidden"
                                        onClick={() => {
                                            if (isActiveSlide) return;
                                            setCurrentSlide(index);
                                            setStartTime(Date.now());
                                            setElapsed(0);
                                        }}
                                    >
                                        {isActiveSlide && (
                                            <Box
                                                as={motion.span}
                                                style={{
                                                    width: isActiveSlide
                                                        ? `${slideProgress}%`
                                                        : "0%",
                                                }}
                                                height="100%"
                                                bgColor="porscheBlack"
                                                display="block"
                                                transition={{ type: "tween" }}
                                            />
                                        )}
                                    </Box>
                                );
                            })}
                        </Flex>
                    )}
                </Box>
            </ModuleSpacer>

            <ModalImage
                alt={mediaCollection?.items[currentImage!]?.alt ?? ""}
                cloudinaryAsset={mediaCollection?.items[currentImage!]?.asset}
                isOpen={currentImage !== undefined && isOpen}
                onClose={onClose}
            />
        </>
    );
};
